import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash, Plus, PencilSimple } from "@phosphor-icons/react";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const empty = { county: "", slug: "", state: "PA", description: "", published: true };

export default function ServiceAreasAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/service-areas").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...a }); setEditId(a.id); setOpen(true); };

  const save = async () => {
    if (!form.county) { toast.error("County is required"); return; }
    const slug = form.slug || `${slugify(form.county)}-county-${form.state.toLowerCase()}`;
    const payload = { county: form.county, slug, state: form.state, description: form.description, published: form.published };
    try {
      if (editId) await api.patch(`/admin/service-areas/${editId}`, payload);
      else await api.post("/admin/service-areas", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service area?")) return;
    await api.delete(`/admin/service-areas/${id}`); load(); toast.success("Deleted");
  };

  return (
    <div data-testid="admin-service-areas">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Service Areas</h1>
        <Button onClick={openNew} data-testid="add-area" className="rounded-full bg-brand-purple text-white hover:bg-brand-purple2">
          <Plus size={18} className="mr-1.5" /> Add County
        </Button>
      </div>
      <p className="mt-1 text-sm text-slate-500">Add or manage the counties CareDeserved serves. Each generates an SEO page.</p>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><th className="px-4 py-3">County</th><th className="px-4 py-3">Slug</th><th className="px-4 py-3">Published</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{a.county} County</td>
                <td className="px-4 py-3 text-slate-500">/service-areas/{a.slug}</td>
                <td className="px-4 py-3">{a.published ? <span className="text-emerald-600">Yes</span> : <span className="text-slate-400">No</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => openEdit(a)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-brand-blue"><PencilSimple size={16} /></button>
                    <button onClick={() => remove(a.id)} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><Trash size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} Service Area</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>County</Label><Input data-testid="a-county" value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>State</Label><Input data-testid="a-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="a-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="flex items-center justify-between"><Label>Published</Label><Switch data-testid="a-published" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /></div>
            <Button onClick={save} data-testid="a-save" className="w-full rounded-full bg-brand-purple text-white hover:bg-brand-purple2">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
